import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetBrokerGroupUsersQuery, useGetUserDetailsQuery } from '@/features/user/userApi';
import { useGetCombinedPipelinesAndStagesQuery } from '@/features/pipelines/pipelineApi';
import { pruneFilters as pruneInstantSearchFilters } from '@/features/table/instantSearchSlice';
import { pruneFilters as pruneClientContactsFilters } from '@/features/table/clientContactsPrefsSlice';
import { pruneFilters as pruneRenewalReportFilters } from '@/features/table/renewalReportTableSlice';
import { pruneFilters as pruneDebtorsFilters } from '@/features/table/debtorsTableSlice';
import { pruneFilters as pruneAuditFilters } from '@/features/table/auditTableSlice';

/**
 * Drops saved filter values (users, broker groups, stages) that no longer exist, e.g. an
 * owner who has left the broker group. Valid values come from the same queries the filter
 * selects use. Waits until users and stages have loaded, and never prunes against an empty
 * load, so valid filters aren't wiped.
 */
export const useValidateStoredFilters = () => {
	const dispatch = useDispatch();
	const email = useSelector((state) => state.msalAccount.account?.username);
	const firebaseReady = useSelector((state) => state.firebase.firebaseReady);

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{ skip: !email || !firebaseReady },
	);
	const { data: users } = useGetBrokerGroupUsersQuery(
		{ userId: userDetails?.userId, showGroup: true },
		{ skip: !userDetails?.userId },
	);
	const { data: pipelineData } = useGetCombinedPipelinesAndStagesQuery(
		{ brokerGroupIds: userDetails?.brokerGroupArray },
		{ skip: !userDetails },
	);

	useEffect(() => {
		const stages = pipelineData?.undefined?.stages ?? [];
		if (!userDetails || !users?.length || !stages.length) return;

		const userIds = users.flatMap((user) => [user.id, user.docId]);
		const brokerGroupIds = userDetails.brokerGroupArray;
		const stageIds = stages.flatMap((stage) => [stage.id, stage.stageId]);

		dispatch(
			pruneInstantSearchFilters({
				owners: userIds,
				clientOwners: userIds,
				brokerGroupId: brokerGroupIds,
				dealStage: stageIds,
				stage: stageIds,
			}),
		);
		dispatch(pruneClientContactsFilters({ ownerIds: userIds, brokerGroupId: brokerGroupIds }));
		dispatch(pruneRenewalReportFilters({ ownerIds: userIds }));
		dispatch(pruneDebtorsFilters({ ownerIds: userIds }));
		dispatch(pruneAuditFilters({ userIds }));
	}, [dispatch, userDetails, users, pipelineData]);
};
